import {
  daysAgo,
  monthsAgo,
  startOfMonthIso,
  startOfWeekIso,
  startOfYearIso,
  today,
} from '../lib/format';

export interface DateRange {
  from: string;
  to: string;
}

interface Props {
  value: DateRange;
  onChange: (range: DateRange) => void;
  /** Floor date for the "All time" preset (e.g. the project's created_at day). */
  minDate?: string;
}

// Fallback floor for "All time" when the caller has no earlier bound to offer.
const ALL_TIME_FLOOR = '2000-01-01';

export function DateRangePicker({ value, onChange, minDate }: Props) {
  const PRESETS: Array<{ label: string; compute: () => DateRange }> = [
    { label: 'Today', compute: () => ({ from: today(), to: today() }) },
    { label: 'Yesterday', compute: () => ({ from: daysAgo(1), to: daysAgo(1) }) },
    { label: 'Last 7 days', compute: () => ({ from: daysAgo(6), to: today() }) },
    { label: 'This week', compute: () => ({ from: startOfWeekIso(), to: today() }) },
    { label: 'This month', compute: () => ({ from: startOfMonthIso(), to: today() }) },
    { label: 'Last 3 months', compute: () => ({ from: monthsAgo(3), to: today() }) },
    { label: 'Last 6 months', compute: () => ({ from: monthsAgo(6), to: today() }) },
    { label: 'This year', compute: () => ({ from: startOfYearIso(), to: today() }) },
    { label: 'Last 12 months', compute: () => ({ from: monthsAgo(12), to: today() }) },
    {
      label: 'All time',
      compute: () => ({ from: (minDate ?? ALL_TIME_FLOOR).slice(0, 10), to: today() }),
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-3 border border-border-light rounded-comfort p-3 bg-white">
      <div className="flex items-center gap-2">
        <span className="mono-label text-black/50">from</span>
        <input
          type="date"
          value={value.from}
          max={value.to}
          onChange={(e) => onChange({ ...value, from: e.target.value })}
          className="bg-transparent border border-border-light rounded-sharp px-2 py-1 font-mono text-[12px] text-black"
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="mono-label text-black/50">to</span>
        <input
          type="date"
          value={value.to}
          min={value.from}
          max={today()}
          onChange={(e) => onChange({ ...value, to: e.target.value })}
          className="bg-transparent border border-border-light rounded-sharp px-2 py-1 font-mono text-[12px] text-black"
        />
      </div>
      <div className="h-4 w-px bg-border-light mx-1" aria-hidden />
      <div className="flex items-center gap-1.5 flex-wrap">
        {PRESETS.map((preset) => {
          const range = preset.compute();
          const active = range.from === value.from && range.to === value.to;
          return (
            <button
              key={preset.label}
              type="button"
              onClick={() => onChange(range)}
              className={`rounded-sharp px-2.5 py-1 font-display text-caption transition-colors ${
                active
                  ? 'bg-midnight text-white'
                  : 'bg-glass-dark text-black hover:bg-black/10'
              }`}
            >
              {preset.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
